import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { CheckCircle, XCircle, Eye, Crown, BriefcaseMetal, User as UserIcon, DownloadSimple } from "@phosphor-icons/react";

const STATUS = ["pending", "approved", "rejected"];

const CLASSES = [
  { value: "guest", label: "Guest", color: "border-white/30 text-white/80", icon: UserIcon },
  { value: "sponsor_team", label: "Sponsor Team", color: "border-cyber-green text-cyber-green", icon: BriefcaseMetal },
  { value: "sponsor_request", label: "Sponsor Request", color: "border-cyber-yellow text-cyber-yellow", icon: Crown },
];

const ClassPill = ({ value, sponsorName }) => {
  const cfg = CLASSES.find((c) => c.value === value) || CLASSES[0];
  if (cfg.value === "guest") return null;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${cfg.color}`} data-testid={`class-pill-${value}`}>
      <Icon size={11} weight="bold" /> {cfg.label}{sponsorName ? ` · ${sponsorName}` : ""}
    </span>
  );
};

const AdminRsvps = () => {
  const [rsvps, setRsvps] = useState([]);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState({ status: "pending", event_id: "" });
  const [active, setActive] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [approveDraft, setApproveDraft] = useState({ attendee_class: "guest", sponsor_name: "" });
  const [reapproveNote, setReapproveNote] = useState("");
  const [reapproveError, setReapproveError] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (filter.status) params.status_filter = filter.status;
    if (filter.event_id) params.event_id = filter.event_id;
    const { data } = await api.get("/admin/rsvps", { params });
    setRsvps(data);
  }, [filter]);

  useEffect(() => { api.get("/admin/events").then(({ data }) => setEvents(data)); }, []);
  useEffect(() => { load(); }, [load]);

  // When the active RSVP changes, prefill the approval-classification draft
  useEffect(() => {
    if (active) {
      setApproveDraft({
        attendee_class: active.attendee_class || "guest",
        sponsor_name: active.sponsor_name || "",
      });
      setReapproveNote("");
      setReapproveError("");
    }
    // Intentionally keyed on id only: re-prefill when a different RSVP opens, not on every object update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  const action = async (rsvp, kind, extra = {}) => {
    setBusyId(rsvp.id);
    try {
      const { data } = await api.post(`/admin/rsvps/${rsvp.id}/${kind}`, { notes: "", ...extra });
      setActive((a) => (a && a.id === rsvp.id ? data : a));
      load();
    } finally {
      setBusyId(null);
    }
  };

  const updateClassification = async (rsvp, attendee_class, sponsor_name) => {
    setBusyId(rsvp.id);
    try {
      const { data } = await api.post(`/admin/rsvps/${rsvp.id}/classification`, { attendee_class, sponsor_name });
      setActive((a) => (a && a.id === rsvp.id ? data : a));
      load();
    } finally {
      setBusyId(null);
    }
  };

  const eventOptions = useMemo(() => events.map((e) => ({ id: e.id, title: e.title })), [events]);

  const exportCsv = async () => {
    const params = {};
    if (filter.status) params.status_filter = filter.status;
    if (filter.event_id) params.event_id = filter.event_id;
    try {
      const res = await api.get("/admin/rsvps/export.csv", { params, responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const filename = match?.[1] || `fintechomni-rsvps-${new Date().toISOString().slice(0,10)}.csv`;
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("CSV export failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  // Sponsor list for the active event (used by the sponsor-team selector)
  const activeEventSponsors = useMemo(() => {
    if (!active) return [];
    const ev = events.find((e) => e.id === active.event_id);
    return ev?.sponsors || [];
  }, [active, events]);

  return (
    <div data-testid="admin-rsvps-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="term-label">/ rsvps</div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-2">Approve queue.</h1>
        </div>
        <div className="flex items-center gap-2 cyber">
          <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="px-3 py-2" data-testid="filter-status">
            <option value="">All statuses</option>
            {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filter.event_id} onChange={(e) => setFilter({ ...filter, event_id: e.target.value })} className="px-3 py-2" data-testid="filter-event">
            <option value="">All events</option>
            {eventOptions.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
          </select>
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 px-3 py-2 hard-border bg-cyber-elevated text-white hover:border-cyber-green hover:text-cyber-green font-mono-tech text-xs uppercase tracking-[0.15em] transition-colors duration-150"
            data-testid="rsvps-export-csv"
            title="Export the currently filtered RSVPs as a CSV file"
          >
            <DownloadSimple size={14} weight="bold" /> Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
        <div className="xl:col-span-7 hard-border bg-cyber-surface">
          <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-cyber-line bg-cyber-elevated font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/60">
            <div className="col-span-4">Name</div>
            <div className="col-span-3">Event</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-3 text-right">Action</div>
          </div>
          {rsvps.length === 0 && <div className="p-8 font-mono-tech text-white/50">/ no entries.</div>}
          {rsvps.map((r) => (
            <div
              key={r.id}
              role="button"
              tabIndex={0}
              onClick={() => setActive(r)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive(r); } }}
              className={`w-full grid grid-cols-12 gap-2 px-5 py-4 border-b border-cyber-line items-center text-left hover:bg-cyber-elevated transition-colors cursor-pointer ${active?.id === r.id ? 'bg-cyber-elevated' : ''}`}
              data-testid={`rsvp-row-${r.id}`}
            >
              <div className="col-span-4">
                <div className="text-white">{r.name}</div>
                <div className="font-mono-tech text-xs text-white/50">{r.email}</div>
              </div>
              <div className="col-span-3 font-mono-tech text-xs text-white/70 truncate">{r.event_title}</div>
              <div className="col-span-2 flex flex-col gap-1 items-start">
                <StatusPill status={r.status} />
                {r.status === "approved" && r.attendee_class && r.attendee_class !== "guest" && (
                  <ClassPill value={r.attendee_class} sponsorName={r.sponsor_name} />
                )}
              </div>
              <div className="col-span-3 flex items-center justify-end gap-2">
                {r.status === "pending" && (
                  <button onClick={(e) => { e.stopPropagation(); action(r, "reject"); }} disabled={busyId === r.id} className="font-mono-tech text-xs text-cyber-red flex items-center gap-1 hover:text-white" data-testid={`reject-${r.id}`}><XCircle size={14} weight="bold" /> Reject</button>
                )}
                <span className="font-mono-tech text-xs text-cyber-yellow tracking-[0.15em] uppercase">Review</span>
                <Eye size={14} className="text-white/40" />
              </div>
            </div>
          ))}
        </div>

        <div className="xl:col-span-5 hard-border bg-cyber-surface p-6 sticky top-24 self-start" data-testid="rsvp-detail-pane">
          {!active && <div className="font-mono-tech text-white/50">/ select an RSVP to inspect.</div>}
          {active && (
            <>
              <div className="term-label">/ inspect</div>
              <h2 className="font-display font-black text-2xl mt-2">{active.name}</h2>
              <div className="font-mono-tech text-xs text-white/60 mt-1">{active.email} · {active.phone}</div>
              <div className="mt-3"><StatusPill status={active.status} /></div>
              <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                <Info k="Event" v={active.event_title} />
                <Info k="Company" v={active.company || "—"} />
                <Info k="Country" v={active.country || "—"} />
                <Info k="Industry" v={active.industry || "—"} />
                <Info k="Position" v={active.position || "—"} />
                <Info k="Role" v={active.attendee_type || "—"} />
                <Info k="Attended before" v={active.attended_before ? "Yes" : "No"} />
                <Info k="Submitted" v={new Date(active.created_at).toLocaleString()} />
                <Info k="Reviewed by" v={active.reviewer_email || "—"} />
              </div>
              {active.looking_for && (
                <div className="mt-5">
                  <div className="term-label mb-2">/ what they're looking for</div>
                  <div className="hard-border bg-cyber-elevated p-3 text-white/85 text-sm leading-relaxed whitespace-pre-line">
                    {active.looking_for}
                  </div>
                </div>
              )}
              <div className="mt-5">
                <div className="term-label mb-2">/ answers</div>
                <div className="hard-border divide-y divide-cyber-line">
                  {Object.entries(active.answers || {}).map(([k, v]) => (
                    <div key={k} className="px-3 py-2">
                      <div className="font-mono-tech text-[10px] text-white/40 uppercase tracking-[0.2em]">{k.slice(0, 8)}</div>
                      <div className="text-white text-sm break-words">{String(v)}</div>
                    </div>
                  ))}
                  {(!active.answers || Object.keys(active.answers).length === 0) && <div className="px-3 py-2 font-mono-tech text-xs text-white/40">no answers</div>}
                </div>
              </div>
              {active.notifications && active.notifications.length > 0 && (
                <div className="mt-5">
                  <div className="term-label mb-2">/ notifications</div>
                  <div className="space-y-1">
                    {active.notifications.map((n, i) => (
                      <div key={n.at ? `${n.at}-${n.kind || n.channel || i}` : i} className="font-mono-tech text-xs text-white/60 flex items-center justify-between hard-border px-3 py-2">
                        <span>{n.channel?.toUpperCase()} → {n.to || ""}</span>
                        <span className={n.status === "sent" ? "text-cyber-green" : n.status === "stubbed" ? "text-cyber-yellow" : "text-cyber-red"}>{n.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {active.status === "pending" && (
                <div className="mt-6 hard-border bg-cyber-elevated p-4 space-y-4">
                  <div>
                    <div className="term-label mb-2">/ classify before approving</div>
                    <div className="grid grid-cols-3 gap-2">
                      {CLASSES.map((c) => {
                        const Icon = c.icon;
                        const sel = approveDraft.attendee_class === c.value;
                        return (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => setApproveDraft((d) => ({ ...d, attendee_class: c.value, sponsor_name: c.value === "sponsor_team" ? d.sponsor_name : "" }))}
                            className={`hard-border p-3 text-left transition-colors duration-150 ${sel ? "bg-white text-cyber-black border-white" : "bg-cyber-black border-cyber-line text-white hover:border-cyber-green"}`}
                            data-testid={`class-btn-${c.value}`}
                          >
                            <div className="flex items-center gap-2">
                              <Icon size={14} weight="bold" />
                              <span className="font-mono-tech text-[11px] uppercase tracking-[0.15em]">{c.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {approveDraft.attendee_class === "sponsor_team" && (
                    <div data-testid="sponsor-picker">
                      <div className="term-label mb-2">/ which sponsor</div>
                      {activeEventSponsors.length > 0 ? (
                        <select
                          value={approveDraft.sponsor_name}
                          onChange={(e) => setApproveDraft((d) => ({ ...d, sponsor_name: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-cyber-black border border-cyber-line text-white"
                          data-testid="sponsor-select"
                        >
                          <option value="">Choose sponsor...</option>
                          {activeEventSponsors.map((s, i) => (
                            <option key={s.name} value={s.name}>{s.name} ({s.tier || "silver"})</option>
                          ))}
                          <option value="__custom__">Other (type below)</option>
                        </select>
                      ) : null}
                      {(activeEventSponsors.length === 0 || approveDraft.sponsor_name === "__custom__") && (
                        <input
                          type="text"
                          placeholder="Sponsor company name"
                          value={approveDraft.sponsor_name === "__custom__" ? "" : approveDraft.sponsor_name}
                          onChange={(e) => setApproveDraft((d) => ({ ...d, sponsor_name: e.target.value }))}
                          className="w-full mt-2 px-3 py-2.5 bg-cyber-black border border-cyber-line text-white"
                          data-testid="sponsor-name-input"
                        />
                      )}
                      <p className="font-mono-tech text-[10px] text-white/40 mt-2">
                        / Tip: add sponsors on the event edit page so they appear in this dropdown.
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        const cls = approveDraft.attendee_class;
                        const sponsor = cls === "sponsor_team" ? (approveDraft.sponsor_name || "").trim() : "";
                        if (cls === "sponsor_team" && !sponsor) {
                          alert("Please pick or enter the sponsor company.");
                          return;
                        }
                        action(active, "approve", { attendee_class: cls, sponsor_name: sponsor });
                      }}
                      disabled={busyId === active.id}
                      className="btn-neon flex-1 justify-center"
                      data-testid="detail-approve-btn"
                    >
                      <CheckCircle weight="bold" size={14} /> Approve
                    </button>
                    <button onClick={() => action(active, "reject")} disabled={busyId === active.id} className="btn-ghost-neon flex-1 justify-center" data-testid="detail-reject-btn">
                      <XCircle weight="bold" size={14} /> Reject
                    </button>
                  </div>
                </div>
              )}

              {active.status === "rejected" && (
                <div className="mt-6 hard-border bg-cyber-elevated p-4 space-y-3" data-testid="reapprove-block">
                  <div className="term-label">/ rejected by mistake?</div>
                  <p className="text-white/60 text-sm">You can switch this request back to approved. The guest will receive the standard approval email with their QR code. A note explaining the change is required (kept for your records).</p>
                  <textarea
                    value={reapproveNote}
                    onChange={(e) => { setReapproveNote(e.target.value); setReapproveError(""); }}
                    placeholder="Why are you changing this status? (required)"
                    rows={3}
                    className="w-full bg-cyber-black hard-border px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cyber-green"
                    data-testid="reapprove-note-input"
                  />
                  {reapproveError && <div className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-cyber-red" data-testid="reapprove-error">/ {reapproveError}</div>}
                  <button
                    onClick={() => {
                      if (!reapproveNote.trim()) { setReapproveError("A note is required to change the status"); return; }
                      action(active, "approve", { notes: reapproveNote.trim() });
                    }}
                    disabled={busyId === active.id}
                    className="btn-neon w-full justify-center"
                    data-testid="reapprove-btn"
                  >
                    <CheckCircle weight="bold" size={14} /> Change to Approved & Send QR Email
                  </button>
                </div>
              )}

              {active.status === "approved" && (
                <div className="mt-6 hard-border bg-cyber-elevated p-4 space-y-3" data-testid="reclassify-block">
                  <div className="flex items-center justify-between">
                    <div className="term-label">/ classification</div>
                    {active.attendee_class && active.attendee_class !== "guest" && (
                      <ClassPill value={active.attendee_class} sponsorName={active.sponsor_name} />
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {CLASSES.map((c) => {
                      const Icon = c.icon;
                      const sel = (active.attendee_class || "guest") === c.value;
                      return (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => updateClassification(active, c.value, c.value === "sponsor_team" ? (active.sponsor_name || "") : "")}
                          disabled={busyId === active.id}
                          className={`hard-border p-2 text-left transition-colors duration-150 ${sel ? "bg-white text-cyber-black border-white" : "bg-cyber-black border-cyber-line text-white hover:border-cyber-green"}`}
                          data-testid={`reclassify-${c.value}`}
                        >
                          <div className="flex items-center gap-2">
                            <Icon size={12} weight="bold" />
                            <span className="font-mono-tech text-[10px] uppercase tracking-[0.15em]">{c.label}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {active.attendee_class === "sponsor_team" && (
                    <div>
                      <div className="term-label mb-1">/ sponsor</div>
                      {activeEventSponsors.length > 0 ? (
                        <select
                          value={active.sponsor_name || ""}
                          onChange={(e) => updateClassification(active, "sponsor_team", e.target.value)}
                          className="w-full px-3 py-2 bg-cyber-black border border-cyber-line text-white"
                          data-testid="reclassify-sponsor-select"
                        >
                          <option value="">Choose sponsor...</option>
                          {activeEventSponsors.map((s, i) => (
                            <option key={s.name} value={s.name}>{s.name} ({s.tier || "silver"})</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          placeholder="Sponsor company name"
                          defaultValue={active.sponsor_name || ""}
                          onBlur={(e) => {
                            if (e.target.value !== (active.sponsor_name || "")) {
                              updateClassification(active, "sponsor_team", e.target.value);
                            }
                          }}
                          className="w-full px-3 py-2 bg-cyber-black border border-cyber-line text-white"
                          data-testid="reclassify-sponsor-input"
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    pending: "border-cyber-yellow text-cyber-yellow",
    approved: "border-cyber-green text-cyber-green",
    rejected: "border-cyber-red text-cyber-red",
  };
  return <span className={`font-mono-tech text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${map[status] || ''}`}>{status}</span>;
};

const Info = ({ k, v }) => (
  <div className="hard-border px-3 py-2">
    <div className="font-mono-tech text-[10px] text-white/40 uppercase tracking-[0.2em]">{k}</div>
    <div className="text-white text-sm">{v}</div>
  </div>
);

export default AdminRsvps;
